<template>
  <div class="sdb">
    <!-- Header: title + period + filter access -->
    <header class="sdb__header">
      <div class="sdb__heading">
        <h1 class="sdb__title">{{ $t('mobileDash.title') }}</h1>
        <div class="sdb__range">{{ rangeLabel }}</div>
      </div>
      <button type="button" class="sdb__pill" @click="sheetOpen = true">
        <span>{{ presetLabel }}</span>
        <i class="fa-solid fa-chevron-down"></i>
        <span v-if="activeFilterCount" class="sdb__badge">{{ activeFilterCount }}</span>
      </button>
    </header>

    <!-- Active filter chips -->
    <div v-if="chips.length" class="sdb__chips">
      <button v-for="chip in chips" :key="chip.key" type="button" class="sdb__chip" @click="removeChip(chip)">
        {{ chip.label }} <i class="fa-solid fa-xmark"></i>
      </button>
    </div>

    <!-- Refresh indicator: filter changes keep the old data visible but dimmed -->
    <div v-if="refreshing" class="sdb__updating">
      <b-spinner small/> {{ $t('mobileDash.updating') }}
    </div>

    <!-- KPIs -->
    <md-section :class="{ 'sdb__dim': summaryLoading && summary }" :error="summaryError" @retry="fetchSummary">
      <md-skeleton v-if="summaryLoading && !summary" variant="kpi"/>
      <template v-else-if="summary">
        <div v-if="isEmpty" class="sdb__empty">
          <i class="fa-regular fa-folder-open"></i>
          <div class="sdb__empty-title">{{ $t('mobileDash.emptyTitle') }}</div>
          <div class="sdb__empty-text">{{ $t('mobileDash.emptyText') }}</div>
        </div>
        <template v-else>
          <!-- Hero: TOTAL deposits INCLUDING the pending ones (tap = that list).
               The approved-only figure sits in its own bar right under it, with
               the pending bar beneath — same three numbers as the Command
               Center hero, stacked for the phone. Every part opens its own list. -->
          <div class="sdb-hero">
            <button type="button" class="sdb-hero__main" @click="openKpiDrill('depositsWithPending')">
              <span class="sdb-hero__value">{{ fullEuro(depositsWithPending) }}</span>
              <span class="sdb-hero__label">{{ $t('mobileDash.totalDeposits') }}<i class="fa-solid fa-chevron-right ml-25"></i></span>
              <span class="sdb-hero__meta">
                {{ $t('mobileDash.withPending') }}
                · {{ $t('mobileDash.net') }} {{ fullEuro(current.netDeposits) }}
              </span>
            </button>
            <button type="button" class="sdb-hero__approved" @click="openKpiDrill('deposits')">
              <span class="sdb-hero__approved-label"><i class="fa-solid fa-circle-check mr-25"></i>{{ $t('financeStatuses.APPROVED') }}</span>
              <span class="sdb-hero__approved-count">{{ wholeNumber(current.depositsCount) }}</span>
              <span class="sdb-hero__approved-amount">{{ fullEuro(current.totalDeposits) }}</span>
              <i class="fa-solid fa-chevron-right sdb-hero__approved-chev"></i>
            </button>
            <div v-if="!filter.department" class="sdb-hero__split">
              <div class="sdb-hero__bar"><span class="sdb-hero__seg" :style="{ width: salesShare + '%' }"></span></div>
              <div class="sdb-hero__legend">
                <button type="button" class="sdb-hero__part" @click="openDepartmentDrill('SALES')">
                  <span class="sdb-hero__part-label sdb-hero__part-label--sales">
                    <i class="fa-solid fa-star mr-25"></i>{{ $t('mobileDash.sales') }} · {{ Math.round(salesShare) }}%
                  </span>
                  <span class="sdb-hero__part-value">{{ fullEuro(current.salesDeposits) }}</span>
                </button>
                <button type="button" class="sdb-hero__part sdb-hero__part--right" @click="openDepartmentDrill('RETENTION')">
                  <span class="sdb-hero__part-label sdb-hero__part-label--ret">
                    {{ $t('mobileDash.retention') }} · {{ Math.round(100 - salesShare) }}%<i class="fa-solid fa-coins ml-25"></i>
                  </span>
                  <span class="sdb-hero__part-value">{{ fullEuro(current.retentionDeposits) }}</span>
                </button>
              </div>
            </div>
            <button type="button" class="sdb-hero__pending" @click="openPendingDrill('DEPOSIT')">
              <span class="sdb-hero__pending-label"><i class="fa-regular fa-clock mr-25"></i>{{ $t('monetaryDashboard.pendingDeposits') }}</span>
              <span class="sdb-hero__pending-count">{{ wholeNumber(current.pendingDepositsCount) }}</span>
              <span class="sdb-hero__pending-amount">{{ fullEuro(current.pendingDepositsAmount) }}</span>
              <i class="fa-solid fa-chevron-right sdb-hero__chev"></i>
            </button>
          </div>

          <div class="sdb__grid">
            <md-kpi-card :label="$t('mobileDash.withdrawals')" :value="fullEuro(absWithdrawals(current))" tone="withdraw"
                         :sub="`${wholeNumber(current.withdrawalsCount)} ${$t('mobileDash.drill.items')}`"
                         tappable @open="openKpiDrill('withdrawals')"/>
            <!-- FTD carries the lead funnel figures (new leads / duplicated) -->
            <md-kpi-card :label="$t('mobileDash.ftd')" :value="wholeNumber(current.ftdCount)" tone="ftd"
                         :sub="ftdSub" tappable @open="openKpiDrill('ftd')"/>
            <!-- STD with its share of the period's FTDs (no period delta on purpose) -->
            <md-kpi-card :label="$t('mobileDash.std')" :value="wholeNumber(current.stdCount)" tone="std"
                         :sub="`${fullEuro(current.stdAmount)} · ${$t('mobileDash.stdOfFtd', { pct: stdOfFtdPct })}`"
                         tappable @open="openKpiDrill('std')"/>
            <!-- TXF (taxes): CRM-bookkeeping money, folded into Total Deposits /
                 Net, never FTD/STD. Same figure the PC dashboards' TXF card shows. -->
            <md-kpi-card :label="$t('monetaryDashboard.txf')" :value="fullEuro(current.txfAmount)" tone="txf"
                         :sub="`${wholeNumber(current.txfCount)} ${$t('mobileDash.drill.items')}`"
                         tappable @open="openKpiDrill('txf')"/>
            <md-kpi-card :label="$t('mobileDash.net')" :value="fullEuro(current.netDeposits)" tone="deposit"
                         :sub="`${$t('mobileDash.ftdAmount')} ${fullEuro(current.ftdAmount)}`"/>
            <!-- Conversion card removed per request — restore:
                 <md-kpi-card :label="$t('mobileDash.conversion')" :value="conversionLabel" tone="conv"/> -->
          </div>

          <!-- Needs attention — pending withdrawals | finance review queue |
               FAKE deposits | FAKE withdrawals. All tappable (same lists as the
               Command Center). Pending DEPOSITS live in the hero above. -->
          <div class="sdb-watch">
            <div class="sdb-watch__title"><i class="fa-solid fa-bell mr-25"></i>{{ $t('mobileDash.attention') }}</div>
            <div class="sdb-watch__grid">
              <button type="button" class="sdb-watch__tile sdb-watch__tile--wd" @click="openPendingDrill('WITHDRAWAL')">
                <span class="sdb-watch__label"><i class="fa-solid fa-arrow-up mr-25"></i>{{ $t('monetaryDashboard.pendingWithdrawals') }}</span>
                <span class="sdb-watch__count">{{ wholeNumber(current.pendingWithdrawalsCount) }}</span>
                <span class="sdb-watch__amount">{{ fullEuro(absAmount(current.pendingWithdrawalsAmount)) }}</span>
              </button>
              <button type="button" class="sdb-watch__tile sdb-watch__tile--review" @click="openPendingReviewDrill">
                <span class="sdb-watch__label"><i class="fa-solid fa-magnifying-glass-dollar mr-25"></i>{{ $t('finance.pendingReview') }}</span>
                <span class="sdb-watch__count">{{ wholeNumber(current.pendingReviewCount) }}</span>
                <span class="sdb-watch__amount">{{ fullEuro(current.pendingReviewAmount) }}</span>
              </button>
              <button type="button" class="sdb-watch__tile sdb-watch__tile--fake" @click="openFakeDrill('DEPOSIT')">
                <span class="sdb-watch__badge">FAKE</span>
                <span class="sdb-watch__label"><i class="fa-solid fa-arrow-down mr-25"></i>{{ $t('Fake Deposits') }}</span>
                <span class="sdb-watch__count">{{ wholeNumber(current.fakeDepositsCount) }}</span>
                <span class="sdb-watch__amount">{{ fullEuro(current.fakeDeposits) }}</span>
              </button>
              <button type="button" class="sdb-watch__tile sdb-watch__tile--fake" @click="openFakeDrill('WITHDRAWAL')">
                <span class="sdb-watch__badge">FAKE</span>
                <span class="sdb-watch__label"><i class="fa-solid fa-arrow-up mr-25"></i>{{ $t('Fake Withdrawals') }}</span>
                <span class="sdb-watch__count">{{ wholeNumber(current.fakeWithdrawalsCount) }}</span>
                <span class="sdb-watch__amount">{{ fullEuro(absAmount(current.fakeWithdrawals)) }}</span>
              </button>
            </div>
          </div>
        </template>
      </template>
    </md-section>

    <!-- Desks: scoreboard cards, ALWAYS grouped by desk (like the affiliate
         dashboard's desk grouping). Tapping a desk opens its per-agent sheet
         with a Sales/Retention switch — the agents no longer live on the page. -->
    <md-section v-if="!rankingsForbidden && rankings" :class="{ 'sdb__dim': rankingsLoading }" :title="$t('mobileDash.byDesk')">
      <md-affiliate-list :rows="(rankings || {}).desks || []" :limit="5" @select="openDeskAgents"/>
    </md-section>

    <!-- Sales vs Retention now lives inside the Total Deposits hero (tappable parts). -->

    <!-- Rankings -->
    <md-section
        v-if="!rankingsForbidden"
        :class="{ 'sdb__dim': rankingsLoading && rankings }"
        :title="$t('mobileDash.performance')"
        :error="rankingsError"
        @retry="fetchRankings"
    >
      <md-skeleton v-if="rankingsLoading && !rankings" variant="list"/>
      <template v-else-if="rankings">
        <div class="sdb__tabs">
          <button
              v-for="tab in rankingTabs" :key="tab.key"
              type="button"
              class="sdb__tab"
              :class="{ 'is-active': rankingTab === tab.key }"
              @click="rankingTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>
        <md-rank-list :items="rankingItems" :limit="5" selectable @select="openRankingDrill"/>
      </template>
    </md-section>

    <!-- Trend -->
    <md-section v-if="summary && !isEmpty" :class="{ 'sdb__dim': summaryLoading }" :title="$t('mobileDash.trend')">
      <md-trend-chart :points="summary.series || []" :is-dark-skin="isDarkSkin"/>
    </md-section>

    <!-- Insights — disabled for now per request; flip SHOW_INSIGHTS to bring it back
         (the computed, MdInsights component and locales are all still in place). -->
    <md-section v-if="showInsights && insights.length" :class="{ 'sdb__dim': summaryLoading || rankingsLoading }" :title="$t('mobileDash.insights')">
      <md-insights :insights="insights"/>
    </md-section>

    <md-filter-sheet :visible="sheetOpen" :value="filter" @close="sheetOpen = false" @input="onFilterApplied"/>
    <md-agent-sheet :visible="deskSheet.visible" :desk="deskSheet.desk" :filters="filter"
                    @close="deskSheet.visible = false" @select="onDeskAgentSelect"/>
    <md-drill-sheet :visible="drill.visible" :title="drill.title" :filters="drill.filters" @close="drill.visible = false"/>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import MdSection from '@/components/mobile/MdSection';
import MdSkeleton from '@/components/mobile/MdSkeleton';
import MdPrimaryKpi from '@/components/mobile/MdPrimaryKpi';
import MdKpiCard from '@/components/mobile/MdKpiCard';
import MdTrendChart from '@/components/mobile/MdTrendChart';
import MdSplitBar from '@/components/mobile/MdSplitBar';
import MdRankList from '@/components/mobile/MdRankList';
import MdAffiliateList from '@/components/mobile/MdAffiliateList';
import MdInsights from '@/components/mobile/MdInsights';
import MdFilterSheet, { defaultFilter } from '@/components/mobile/MdFilterSheet';
import MdDrillSheet from '@/components/mobile/MdDrillSheet';
import MdAgentSheet from '@/components/mobile/MdAgentSheet';
import { fullEuro, wholeNumber, pct, deltaPct } from '@/components/mobile/mdFormat';

export default {
  name: 'MobileSuperDashboard',
  components: {
    MdSection, MdSkeleton, MdPrimaryKpi, MdKpiCard, MdTrendChart,
    MdSplitBar, MdRankList, MdAffiliateList, MdInsights, MdFilterSheet, MdDrillSheet, MdAgentSheet,
  },
  mixins: [mixinBase, mixinNotifications],
  data() {
    return {
      // Insights section is parked, not deleted — set true to re-enable.
      showInsights: false,
      filter: defaultFilter(this.$moment),
      sheetOpen: false,
      summary: null,
      summaryLoading: false,
      summaryError: null,
      rankings: null,
      rankingsLoading: false,
      rankingsError: null,
      rankingsForbidden: false,
      rankingTab: 'organizations',
      deskSheet: { visible: false, desk: '' },
      drill: { visible: false, title: '', filters: {} },
    };
  },
  computed: {
    ...mapGetters('data', ['getOrganizationNameById', 'getBusinessUnitNameById', 'allOrganizations', 'allBusinessUnits']),
    ...mapGetters('mobileDashboard', { storedFilter: 'filter' }),
    isDarkSkin() {
      return this.$store.state.appConfig.layout.skin === 'dark';
    },
    // True on RE-loads only (data already on screen) — first loads show skeletons
    refreshing() {
      return (this.summaryLoading && this.summary != null)
          || (this.rankingsLoading && this.rankings != null);
    },
    current() {
      return (this.summary && this.summary.current) || {};
    },
    // Headline figure: what the period's deposits come to once every pending
    // deposit is approved. The approved-only bar under it keeps both visible.
    depositsWithPending() {
      return (this.current.totalDeposits || 0) + (this.current.pendingDepositsAmount || 0);
    },
    previous() {
      return (this.summary && this.summary.previous) || {};
    },
    isEmpty() {
      if (!this.summary) return false;
      const c = this.current;
      return !(c.totalDeposits || 0) && !(c.ftdCount || 0) && !(c.clientsCount || 0)
          && !Math.abs(c.totalWithdrawals || 0);
    },
    rangeLabel() {
      const range = this.filter.range || {};
      if (!range.from || !range.until) return '';
      const from = this.$moment(range.from);
      const until = this.$moment(range.until);
      const sameYear = from.year() === until.year();
      return `${from.format('MMM D')} – ${until.format(sameYear ? 'MMM D' : 'MMM D, YYYY')}`;
    },
    presetLabel() {
      if (this.filter.preset === 'custom') return this.$t('mobileDash.customRange');
      return this.$t(`filters.picker.${this.filter.preset || 'thisMonth'}`);
    },
    activeFilterCount() {
      let count = 0;
      if (this.filter.department) count++;
      if ((this.filter.desks || []).length) count++;
      if ((this.filter.orgIds || []).length) count++;
      if ((this.filter.buIds || []).length) count++;
      return count;
    },
    chips() {
      const chips = [];
      if (this.filter.department) {
        chips.push({ key: 'department', label: this.$t(`mobileDash.${this.filter.department.toLowerCase()}`) });
      }
      (this.filter.desks || []).forEach(desk => chips.push({ key: `desk:${desk}`, label: desk }));
      (this.filter.orgIds || []).forEach(id =>
          chips.push({ key: `org:${id}`, label: this.getOrganizationNameById(id) || id }));
      (this.filter.buIds || []).forEach(id =>
          chips.push({ key: `bu:${id}`, label: this.getBusinessUnitNameById(id) || id }));
      return chips;
    },
    withdrawalsDelta() {
      return deltaPct(Math.abs(this.current.totalWithdrawals || 0), Math.abs(this.previous.totalWithdrawals || 0) || null);
    },
    conversionLabel() {
      return pct(this.conversionOf(this.current));
    },
    conversionDelta() {
      return deltaPct(this.conversionOf(this.current), this.conversionOf(this.previous));
    },
    departmentParts() {
      return [
        { label: this.$t('mobileDash.sales'), value: this.current.salesDeposits || 0, color: '#7367f0' },
        { label: this.$t('mobileDash.retention'), value: this.current.retentionDeposits || 0, color: '#28c76f' },
      ];
    },
    // Hero split bar: sales share of (sales + retention) deposits
    salesShare() {
      const sales = this.current.salesDeposits || 0;
      const total = sales + (this.current.retentionDeposits || 0);
      if (total <= 0) return 50;
      return Math.min(100, Math.max(0, (sales / total) * 100));
    },
    // STD as a share of the period's FTDs (100 FTD / 40 STD → 40%)
    stdOfFtdPct() {
      const ftd = this.current.ftdCount || 0;
      if (!ftd) return 0;
      return Math.round(((this.current.stdCount || 0) / ftd) * 100);
    },
    // FTD conversion: FTDs as a share of the period's new leads
    ftdOfLeadsPct() {
      const leads = this.current.clientsCount || 0;
      if (!leads) return 0;
      return Math.round(((this.current.ftdCount || 0) / leads) * 100);
    },
    // FTD card sub-line: money + the lead funnel (new leads / conversion / duplicated)
    ftdSub() {
      const parts = [fullEuro(this.current.ftdAmount),
        `${wholeNumber(this.current.clientsCount)} ${this.$t('mobileDash.newClients').toLowerCase()}`,
        `${this.ftdOfLeadsPct}% ${this.$t('mobileDash.ofLeads')}`];
      if (this.current.duplicatedCount) {
        parts.push(`${wholeNumber(this.current.duplicatedCount)} ${this.$t('affiliateDashboard.duplicated').toLowerCase()}`);
      }
      return parts.join(' · ');
    },
    rankingTabs() {
      // Desks got their own always-on section above — this one splits the
      // rest: by organization (default) and by business unit.
      return [
        { key: 'organizations', label: this.$t('mobileDash.tabs.organizations') },
        { key: 'businessUnits', label: this.$t('mobileDash.tabs.businessUnits') },
      ];
    },
    rankingItems() {
      const rows = (this.rankings || {})[this.rankingTab] || [];
      return rows.map(row => ({
        key: row.key,
        label: row.label,
        stats: this.rowStats(row),
        value: fullEuro(row.netTotal),
        raw: row,
      }));
    },
    insights() {
      if (!this.summary || this.isEmpty) return [];
      const out = [];
      const netDelta = this.delta('netDeposits');
      const ftdDelta = this.delta('ftdCount');
      if (netDelta != null && netDelta >= 10) {
        out.push({
          tone: 'up', icon: 'fa-solid fa-arrow-trend-up',
          title: this.$t('mobileDash.insight.growthTitle'),
          text: this.$t('mobileDash.insight.growthText', { pct: netDelta.toFixed(1) }),
        });
      } else if (netDelta != null && netDelta <= -10) {
        out.push({
          tone: 'down', icon: 'fa-solid fa-arrow-trend-down',
          title: this.$t('mobileDash.insight.declineTitle'),
          text: this.$t('mobileDash.insight.declineText', { pct: Math.abs(netDelta).toFixed(1) }),
        });
      }
      if (ftdDelta != null && ftdDelta <= -10) {
        out.push({
          tone: 'down', icon: 'fa-solid fa-user-minus',
          title: this.$t('mobileDash.insight.ftdDropTitle'),
          text: this.$t('mobileDash.insight.ftdDropText', { pct: Math.abs(ftdDelta).toFixed(1) }),
        });
      } else if (ftdDelta != null && ftdDelta >= 10) {
        out.push({
          tone: 'up', icon: 'fa-solid fa-user-plus',
          title: this.$t('mobileDash.insight.ftdUpTitle'),
          text: this.$t('mobileDash.insight.ftdUpText', { pct: ftdDelta.toFixed(1) }),
        });
      }
      const topDesk = (((this.rankings || {}).desks) || [])[0];
      if (topDesk && topDesk.netTotal > 0) {
        out.push({
          tone: 'star', icon: 'fa-solid fa-trophy',
          title: this.$t('mobileDash.insight.topDeskTitle'),
          text: this.$t('mobileDash.insight.topDeskText', { desk: topDesk.label, net: fullEuro(topDesk.netTotal) }),
        });
      }
      const topAffiliate = (((this.rankings || {}).affiliates) || []).filter(a => a.key !== 'duplicated')[0];
      if (topAffiliate && topAffiliate.ftds > 0) {
        out.push({
          tone: 'info', icon: 'fa-solid fa-handshake',
          title: this.$t('mobileDash.insight.topAffiliateTitle'),
          text: this.$t('mobileDash.insight.topAffiliateText', { affiliate: topAffiliate.label, count: topAffiliate.ftds }),
        });
      }
      return out.slice(0, 4);
    },
  },
  created() {
    if (this.storedFilter) {
      this.filter = JSON.parse(JSON.stringify(this.storedFilter));
    } else {
      this.setFilter(JSON.parse(JSON.stringify(this.filter)));
    }
    this.fetchAll();
  },
  methods: {
    ...mapActions('mobileDashboard', ['getSummary', 'getRankings']),
    ...mapMutations('mobileDashboard', ['setFilter']),
    fullEuro,
    wholeNumber,
    conversionOf(kpis) {
      if (!kpis || !kpis.clientsCount) return null;
      return ((kpis.ftdCount || 0) / kpis.clientsCount) * 100;
    },
    absWithdrawals(kpis) {
      return Math.abs(kpis.totalWithdrawals || 0);
    },
    // Withdrawal sums are stored negative — the pending card shows them positive.
    absAmount(value) {
      return Math.abs(value || 0);
    },
    delta(field) {
      return deltaPct(this.current[field], this.previous[field]);
    },
    // Desk/brand/unit rows: counts only — conversion lives in the affiliate section
    rowStats(row) {
      const stats = [
        { value: row.ftds || 0, label: 'FTD', tone: 'ftd' },
        { value: row.leads || 0, label: this.$t('mobileDash.leadsLabel'), tone: 'leads' },
      ];
      // TXF is folded into the row's net total; this chip says how much of that
      // total is TXF, so a unit or organization carrying only TXF money reads
      // as its real figure instead of a flat 0.
      if (row.txf) {
        stats.push({ value: fullEuro(row.txf), label: 'TXF', tone: 'txf' });
      }
      return stats;
    },

    // ---------------------------------------------------------- drill-down
    /**
     * Mirrors the desktop SalesDashboard kpiTxFilters(): the drill list shows
     * the same transactions the KPI counted. Desk/department are resolved to
     * org/BU ids client-side, matching the backend's own scoping.
     */
    drillScope() {
      const selectedOrgIds = (this.filter.orgIds && this.filter.orgIds.length) ? this.filter.orgIds : null;
      let orgIds = selectedOrgIds;
      if ((this.filter.desks || []).length) {
        const desks = new Set(this.filter.desks);
        const deskOrgIds = (this.allOrganizations || [])
            .filter(org => desks.has(org.desk && org.desk.length ? org.desk : 'No Desk'))
            .map(org => org.id);
        orgIds = selectedOrgIds ? deskOrgIds.filter(id => selectedOrgIds.includes(id)) : deskOrgIds;
        if (!orgIds.length) orgIds = ['0'];
      }
      let buIds = (this.filter.buIds && this.filter.buIds.length) ? this.filter.buIds : null;
      if (this.filter.department) {
        const typed = (this.allBusinessUnits || [])
            .filter(bu => bu.type === this.filter.department)
            .filter(bu => !orgIds || orgIds.includes(bu.orgId))
            .map(bu => bu.id)
            .filter(id => !buIds || buIds.includes(id));
        buIds = typed.length ? typed : ['0'];
      }
      return { orgIds, businessUnit: buIds };
    },
    kpiTxFilters(extra) {
      const filters = {
        type: 'DEPOSIT',
        action: ['CREDIT_LINE', 'TAXES'],
        status: ['APPROVED'],
        isTest: false,
        realType: ['REAL'],
        ...this.drillScope(),
        ...(extra || {}),
      };
      const range = this.filter.range || {};
      if (range.from) {
        filters.registrationTime = {
          start: this.$moment(range.from).startOf('day').toServerDateTimeFormat(),
          end: this.$moment(range.until || range.from).endOf('day').toServerDateTimeFormat(),
        };
      }
      return filters;
    },
    openDrill(title, extra) {
      this.drill = { visible: true, title, filters: this.kpiTxFilters(extra) };
    },
    openKpiDrill(kind) {
      if (kind === 'depositsWithPending') {
        // Headline total: approved deposits AND the ones still waiting, so the
        // modal's sum is the same number the hero shows.
        this.openDrill(this.$t('mobileDash.withPending'), { status: ['APPROVED', 'REQUEST', 'PENDING'] });
      } else if (kind === 'ftd') {
        this.openDrill(this.$t('mobileDash.ftd'), { ftdFamily: true, excludeRefundedFtdClients: true, action: ['CREDIT_LINE'] });
      } else if (kind === 'std') {
        this.openDrill(this.$t('mobileDash.std'), { isStd: true, action: ['CREDIT_LINE'] });
      } else if (kind === 'txf') {
        // TXF (taxes) only — the same list the PC dashboards' TXF card opens
        this.openDrill(this.$t('monetaryDashboard.txf'), { action: ['TAXES'] });
      } else if (kind === 'withdrawals') {
        this.openDrill(this.$t('mobileDash.withdrawals'), { type: 'WITHDRAWAL', action: ['CREDIT_LINE'] });
      } else {
        this.openDrill(this.$t('mobileDash.deposits'), {});
      }
    },
    // Pending approvals (website REQUESTs + CRM-created PENDING rows) by type
    openPendingDrill(type) {
      const label = type === 'DEPOSIT'
          ? this.$t('monetaryDashboard.pendingDeposits')
          : this.$t('monetaryDashboard.pendingWithdrawals');
      this.openDrill(label, { type, status: ['REQUEST', 'PENDING'], action: ['CREDIT_LINE'] });
    },
    // Hero split: sales money = FTD money, retention = the retention bucket
    // (the same two lists the Sales Dashboard / Command Center open).
    openDepartmentDrill(department) {
      if (department === 'SALES') {
        this.openDrill(this.$t('mobileDash.sales'), { ftdFamily: true, excludeRefundedFtdClients: true, action: ['CREDIT_LINE'] });
      } else {
        this.openDrill(this.$t('mobileDash.retention'), { retentionBucket: true, action: ['CREDIT_LINE'] });
      }
    },
    // Finance review queue — the Finance Dashboard's "Pending review" bucket
    openPendingReviewDrill() {
      this.openDrill(this.$t('finance.pendingReview'), { status: ['APPROVED', 'CANCELED'], financeStatus: ['PENDING'] });
    },
    // FAKE money — approved transactions marked realType FAKE
    openFakeDrill(type) {
      if (type === 'DEPOSIT') {
        this.openDrill(this.$t('Fake Deposits'), { realType: ['FAKE'] });
      } else {
        this.openDrill(this.$t('Fake Withdrawals'), { type: 'WITHDRAWAL', realType: ['FAKE'], action: ['CREDIT_LINE'] });
      }
    },
    // Desk card → the per-agent sheet (Sales/Retention inside).
    openDeskAgents(row) {
      const raw = row.raw || row || {};
      const desk = raw.label || raw.key;
      if (!desk) return;
      this.deskSheet = { visible: true, desk };
    },
    /** Agent tapped inside the desk sheet → transactions drill scoped to that desk + agent. */
    onDeskAgentSelect(item) {
      const agent = item.raw || {};
      if (!agent.userId) return;
      if (agent.kind === 'txf') {
        // that agent's TXF rows — deposits AND withdrawals, so the sum is his TXF figure
        this.openDrill(`${this.deskSheet.desk} · ${item.label} · TXF`,
            { type: null, action: ['TAXES'], userIds: [agent.userId], orgIds: this.deskOrgIds(this.deskSheet.desk) });
        return;
      }
      const extra = agent.kind === 'sales'
          ? { ftdFamily: true, excludeRefundedFtdClients: true, action: ['CREDIT_LINE'], userIds: [agent.userId] }
          : { isFtd: false, action: ['CREDIT_LINE'], userIds: [agent.userId] };
      this.openDrill(`${this.deskSheet.desk} · ${item.label}`, { ...extra, orgIds: this.deskOrgIds(this.deskSheet.desk) });
    },
    /** Client-side desk → orgIds resolution (same rule as the backend's deskOf). */
    deskOrgIds(desk) {
      const ids = (this.allOrganizations || [])
          .filter(org => (org.desk && org.desk.length ? org.desk : 'No Desk') === desk)
          .map(org => org.id);
      return ids.length ? ids : ['0'];
    },
    openRankingDrill(item) {
      const row = item.raw || {};
      if (this.rankingTab === 'businessUnits') {
        this.openDrill(item.label, { businessUnit: [row.key] });
      } else {
        this.openDrill(item.label, { orgIds: [row.key] });
      }
    },
    onFilterApplied(filter) {
      this.filter = filter;
      this.setFilter(JSON.parse(JSON.stringify(filter)));
      this.fetchAll();
    },
    removeChip(chip) {
      const filter = JSON.parse(JSON.stringify(this.filter));
      if (chip.key === 'department') filter.department = null;
      else if (chip.key.startsWith('desk:')) filter.desks = filter.desks.filter(d => `desk:${d}` !== chip.key);
      else if (chip.key.startsWith('org:')) filter.orgIds = filter.orgIds.filter(id => `org:${id}` !== chip.key);
      else if (chip.key.startsWith('bu:')) filter.buIds = filter.buIds.filter(id => `bu:${id}` !== chip.key);
      this.onFilterApplied(filter);
    },
    fetchAll() {
      this.fetchSummary();
      this.fetchRankings();
    },
    fetchSummary() {
      this.summaryLoading = true;
      this.summaryError = null;
      this.getSummary(this.filter)
          .then(summary => {
            this.summary = summary;
          })
          .catch(err => {
            this.summaryError = this.$t('mobileDash.summaryError');
            console.error('mobile summary error', err, this.getErrorDescription(err));
          })
          .finally(() => {
            this.summaryLoading = false;
          });
    },
    fetchRankings() {
      if (this.rankingsForbidden) return;
      this.rankingsLoading = true;
      this.rankingsError = null;
      this.getRankings(this.filter)
          .then(rankings => {
            this.rankings = rankings;
          })
          .catch(err => {
            // No affiliate permission → hide the sections instead of erroring
            if (err.response && err.response.status === 403) {
              this.rankingsForbidden = true;
            } else {
              this.rankingsError = this.$t('mobileDash.rankingsError');
            }
          })
          .finally(() => {
            this.rankingsLoading = false;
          });
    },
  },
};
</script>

<style scoped>
.sdb {
  max-width: 520px;
  margin: 0 auto;
  padding: 0.25rem 0.15rem 2rem;
}
.sdb__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
}
.sdb__title {
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0;
  color: var(--md-strong, #24292f);
}
/* Small accent bar under the title — readable everywhere, still distinctive */
.sdb__title::after {
  content: '';
  display: block;
  width: 34px;
  height: 3px;
  margin-top: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, #7367f0, #00cfe8);
}
.dark-layout .sdb__title {
  color: #d0d2d6;
}
.sdb__range {
  font-size: 12px;
  color: var(--md-muted, #8a919c);
  margin-top: 1px;
}
.sdb__pill {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid rgba(34, 41, 47, 0.12);
  background: var(--md-card-bg, #fff);
  border-radius: 999px;
  padding: 0.45rem 0.9rem;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  transition: transform 0.08s ease;
}
.sdb__pill:active { transform: scale(0.96); }
.sdb__pill i { font-size: 10px; opacity: 0.6; }
.sdb__badge {
  position: absolute;
  top: -5px;
  right: -4px;
  min-width: 17px;
  height: 17px;
  border-radius: 999px;
  background: var(--primary, #7367f0);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}
.sdb__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
}
.sdb__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: rgba(115, 103, 240, 0.1);
  color: var(--primary, #7367f0);
  border-radius: 999px;
  padding: 0.25rem 0.65rem;
  font-size: 11.5px;
  font-weight: 600;
}
.sdb__chip i { font-size: 9px; opacity: 0.7; }
.sdb__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
}

/* ===== Pending approvals card — full width like the net card, display-only ===== */
/* ===== Hero: Total deposits + sales/retention split + pending deposits ===== */
.sdb-hero {
  margin-bottom: 0.65rem;
  padding: 0.85rem 0.7rem 0.75rem;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(115, 103, 240, 0.1) 0%, rgba(0, 207, 232, 0.07) 100%);
  border: 1px solid rgba(115, 103, 240, 0.22);
  box-shadow: 0 2px 4px rgba(115, 103, 240, 0.08), 0 8px 18px rgba(115, 103, 240, 0.14);
}
.dark-layout .sdb-hero {
  background: linear-gradient(135deg, rgba(115, 103, 240, 0.18) 0%, rgba(0, 207, 232, 0.1) 100%);
  border-color: rgba(115, 103, 240, 0.35);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.28), 0 8px 18px rgba(0, 0, 0, 0.24);
}
.sdb-hero__main {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  text-align: center;
  -webkit-tap-highlight-color: transparent;
}
.sdb-hero__main:active { opacity: 0.75; }
.sdb-hero__value {
  font-size: 30px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: var(--md-strong, #24292f);
  white-space: nowrap;
}
@media (max-width: 380px) {
  .sdb-hero__value { font-size: 25px; }
}
.sdb-hero__label {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6a5fe0;
}
.sdb-hero__label i { font-size: 10px; }
.dark-layout .sdb-hero__label { color: #9d94f5; }
.sdb-hero__meta {
  margin-top: 3px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--md-muted, #8a919c);
  font-variant-numeric: tabular-nums;
}
.sdb-hero__split { margin-top: 0.7rem; }
.sdb-hero__bar {
  height: 8px;
  border-radius: 999px;
  background: rgba(40, 199, 111, 0.35);
  overflow: hidden;
}
.sdb-hero__seg {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #8f85f5, #7367f0);
  transition: width 0.4s ease;
}
.sdb-hero__legend {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.4rem;
}
.sdb-hero__part {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  border: 0;
  background: transparent;
  padding: 0.2rem 0.3rem;
  border-radius: 8px;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
}
.sdb-hero__part:active { background: rgba(115, 103, 240, 0.12); }
.sdb-hero__part--right { align-items: flex-end; text-align: right; }
.sdb-hero__part-label {
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.sdb-hero__part-label--sales { color: #6a5fe0; }
.sdb-hero__part-label--ret { color: #1f9d61; }
.dark-layout .sdb-hero__part-label--sales { color: #9d94f5; }
.sdb-hero__part-value {
  font-size: 14px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--md-strong, #24292f);
}
/* Approved-only bar: same shape as the pending bar below it, green instead of
   amber, so the headline (incl. pending) and the approved figure read as one
   pair. Both are tappable and open their own transaction list. */
.sdb-hero__approved {
  margin-top: 0.7rem;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.65rem;
  border-radius: 10px;
  border: 1px solid rgba(40, 199, 111, 0.4);
  background: rgba(40, 199, 111, 0.13);
  -webkit-tap-highlight-color: transparent;
}
.sdb-hero__approved:active { background: rgba(40, 199, 111, 0.25); }
.sdb-hero__approved-label { flex: 1; text-align: left; font-size: 11.5px; font-weight: 700; color: #1f9d61; }
.sdb-hero__approved-count { font-size: 18px; font-weight: 800; font-variant-numeric: tabular-nums; color: var(--md-strong, #24292f); }
.sdb-hero__approved-amount { font-size: 12.5px; font-weight: 700; font-variant-numeric: tabular-nums; color: #1f9d61; }
.sdb-hero__approved-chev { font-size: 10px; color: #1f9d61; opacity: 0.8; }
.dark-layout .sdb-hero__approved-label,
.dark-layout .sdb-hero__approved-amount,
.dark-layout .sdb-hero__approved-chev { color: #48d989; }
.sdb-hero__pending {
  margin-top: 0.7rem;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.65rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 159, 67, 0.4);
  background: rgba(255, 159, 67, 0.14);
  -webkit-tap-highlight-color: transparent;
}
.sdb-hero__pending:active { background: rgba(255, 159, 67, 0.26); }
.sdb-hero__pending-label { flex: 1; text-align: left; font-size: 11.5px; font-weight: 700; color: #c47b28; }
.sdb-hero__pending-count { font-size: 18px; font-weight: 800; font-variant-numeric: tabular-nums; color: var(--md-strong, #24292f); }
.sdb-hero__pending-amount { font-size: 12.5px; font-weight: 700; font-variant-numeric: tabular-nums; color: #c47b28; }
.sdb-hero__chev { font-size: 10px; color: #c47b28; opacity: 0.8; }
.dark-layout .sdb-hero__pending-label,
.dark-layout .sdb-hero__pending-amount,
.dark-layout .sdb-hero__chev { color: #ffb976; }

/* ===== Needs attention — 2x2 tappable tiles ===== */
.sdb-watch {
  margin-top: 0.65rem;
  padding: 0.7rem 0.6rem 0.7rem;
  border-radius: 12px;
  background: rgba(138, 145, 156, 0.07);
  border: 1px solid rgba(138, 145, 156, 0.2);
}
.dark-layout .sdb-watch { background: rgba(255, 255, 255, 0.04); border-color: rgba(255, 255, 255, 0.1); }
.sdb-watch__title {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--md-muted, #8a919c);
  margin-bottom: 0.5rem;
}
.sdb-watch__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}
.sdb-watch__tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  min-width: 0;
  padding: 0.55rem 0.6rem;
  border-radius: 10px;
  border: 1px solid transparent;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
}
.sdb-watch__tile:active { opacity: 0.75; }
.sdb-watch__tile--wd {
  background: rgba(234, 84, 85, 0.1);
  border-color: rgba(234, 84, 85, 0.35);
}
.sdb-watch__tile--wd .sdb-watch__label,
.sdb-watch__tile--wd .sdb-watch__amount { color: #d64545; }
.sdb-watch__tile--review {
  background: rgba(255, 159, 67, 0.12);
  border-color: rgba(255, 159, 67, 0.4);
}
.sdb-watch__tile--review .sdb-watch__label,
.sdb-watch__tile--review .sdb-watch__amount { color: #c47b28; }
.sdb-watch__tile--fake {
  background: repeating-linear-gradient(135deg, rgba(240, 134, 43, 0.1) 0 8px, rgba(240, 134, 43, 0.05) 8px 16px);
  border: 1px dashed rgba(240, 134, 43, 0.7);
}
.sdb-watch__tile--fake .sdb-watch__label,
.sdb-watch__tile--fake .sdb-watch__amount { color: #c9641a; }
.sdb-watch__badge {
  position: absolute;
  top: 5px;
  right: 6px;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: #ff9f43;
  color: #fff;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.14em;
  line-height: 1.6;
}
.sdb-watch__label {
  font-size: 10.5px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.sdb-watch__count {
  font-size: 20px;
  font-weight: 800;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  color: var(--md-strong, #24292f);
}
.sdb-watch__amount { font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums; }
.dark-layout .sdb-watch__count { color: #d0d2d6; }
.dark-layout .sdb-watch__tile--fake .sdb-watch__label,
.dark-layout .sdb-watch__tile--fake .sdb-watch__amount { color: #ffb976; }
.sdb__tabs {
  display: inline-flex;
  gap: 2px;
  margin-bottom: 0.5rem;
  padding: 3px;
  background: rgba(138, 145, 156, 0.1);
  border-radius: 8px;
}
.sdb__tab {
  border: none;
  background: transparent;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--md-muted, #8a919c);
}
.sdb__tab.is-active {
  background: var(--md-card-bg, #fff);
  color: var(--md-strong, #24292f);
  box-shadow: 0 1px 3px rgba(34, 41, 47, 0.12);
}
.sdb__dim {
  opacity: 0.45;
  pointer-events: none;
  transition: opacity 0.2s ease;
}
.sdb__updating {
  position: fixed;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1080;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--md-card-bg, #fff);
  border: 1px solid rgba(34, 41, 47, 0.1);
  box-shadow: 0 4px 14px rgba(34, 41, 47, 0.16);
  border-radius: 999px;
  padding: 0.35rem 0.9rem;
  font-size: 12px;
  font-weight: 600;
  animation: sdb-updating-in 0.2s ease;
}
@keyframes sdb-updating-in {
  from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}
.dark-layout .sdb__updating {
  --md-card-bg: #283046;
  border-color: #3b4253;
  color: #d0d2d6;
}

.sdb__empty {
  text-align: center;
  padding: 2.2rem 1rem;
  border: 1px dashed rgba(138, 145, 156, 0.35);
  border-radius: 12px;
}
.sdb__empty i {
  font-size: 26px;
  color: var(--md-muted, #8a919c);
  opacity: 0.7;
}
.sdb__empty-title {
  margin-top: 0.5rem;
  font-size: 14px;
  font-weight: 700;
}
.sdb__empty-text {
  margin-top: 2px;
  font-size: 12px;
  color: var(--md-muted, #8a919c);
}

.dark-layout .sdb__pill,
.dark-layout .sdb__tab.is-active {
  --md-card-bg: #283046;
  border-color: #3b4253;
  color: #d0d2d6;
}

/* Dark skin tone tokens for the page's OWN blocks. Every Md* child component
   redefines --md-strong on its dark root, but this file's hero / watch tiles /
   tabs read the same var without ever setting it, so the big numbers (Total
   deposits above all) fell back to the light-skin near-black. */
.dark-layout .sdb {
  --md-strong: #d0d2d6;
}
</style>
